import { useState, useEffect } from 'react';
import { NetworkStatus } from 'apollo-client';
import type { QueryResult } from 'react-apollo';
import { useQuery } from '@apollo/react-hooks';
import type { DocumentNode } from 'graphql';

import { expValEquals } from '@confluence/feature-experiments';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager';

import { getInitialPageSize } from './initialPageSizeConstant';

type FeedDropdownStorageConfig<TValue> = {
	defaultValue: TValue;
	sessionStorageKey: string;
	skipQuery?: boolean;
};

type FeedDropdownQueryConfig<TVariables, TValue> = {
	query: DocumentNode;
	variables: (value: TValue) => Omit<TVariables, 'first'>;
};

export function useFeedDropdownStorage<TData, TVariables, TValue>(
	config: FeedDropdownStorageConfig<TValue>,
	queryConfig: FeedDropdownQueryConfig<TVariables, TValue>,
): {
	value: TValue;
	setValue: (newValue: TValue) => void;
	query: QueryResult<TData, TVariables>;
} {
	const { defaultValue, sessionStorageKey, skipQuery } = config;
	const isSSR = process.env.REACT_SSR || window.__SSR_RENDERED__;

	// Initialize state with session storage or default
	const [value, setValue] = useState((): TValue => {
		if (!isSSR) {
			const sessionValue = sessionStorage.getItem(sessionStorageKey);
			if (
				sessionValue &&
				sessionValue !== defaultValue &&
				expValEquals('confluence_feeds_sorting_retention_experiment', 'cohort', 'test')
			) {
				return sessionValue as TValue;
			}
		}
		return defaultValue;
	});

	const query = useQuery<TData, TVariables>(queryConfig.query, {
		variables: {
			first: getInitialPageSize(),
			...queryConfig.variables(value),
		} as TVariables,
		context: { single: true },
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
		skip: skipQuery,
	});

	useEffect(() => {
		// If refetching, reset to default and update session storage, else set the value
		const valueToSet =
			query.networkStatus === NetworkStatus.loading || query.networkStatus === NetworkStatus.refetch
				? defaultValue
				: value;

		setValue(valueToSet);
		sessionStorage.setItem(sessionStorageKey, valueToSet as string);
	}, [query.networkStatus, value, defaultValue, sessionStorageKey]);

	return { value, setValue, query };
}
